import type {
  ChatDocumentExportFormatDTO,
  ChatDocumentExportResultDTO,
  ConversationDTO,
  ConversationMessagesDTO,
  RenameConversationDTO,
  UpdateConversationRagDTO,
} from '@shared/schemas/chat';
import {
  chatRuntimeDefaultsSchema,
  type ChatRuntimeDefaultsDTO,
} from '@shared/schemas/runtime-settings';
import type { UpdateConversationWebSearchDTO } from '@shared/schemas/web-tools';
import type {
  ConversationFeedbackListDTO,
  MessageFeedbackDTO,
  UpsertMessageFeedbackInput,
} from '@shared/schemas/message-feedback';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';
import type { ConversationListResponse } from '../types';

type ApiSuccessResponse<T> = {
  data: T;
  success: true;
};

type ApiErrorPayload = {
  error?: {
    code?: string;
    details?: Record<string, unknown>;
    message?: string;
  };
  success?: false;
};

function createApiUrl(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  const url = new URL(path, apiBaseUrl);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      url.searchParams.set(key, String(value));
    });
  }

  return url;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as (T & ApiErrorPayload) | ApiErrorPayload | null;

  if (!response.ok || payload === null) {
    const fallbackMessage = response.statusText || 'La requête a échoué.';
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? fallbackMessage,
      status: response.status,
    });
  }

  return payload as T;
}

export async function deleteConversation(conversationId: string) {
  const response = await fetch(createApiUrl(`/api/chat/conversations/${conversationId}`), {
    credentials: 'include',
    method: 'DELETE',
  });

  return parseJsonResponse<{ success: true }>(response);
}

export async function getConversation(conversationId: string) {
  const response = await fetch(createApiUrl(`/api/chat/conversations/${conversationId}`), {
    credentials: 'include',
    method: 'GET',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<ConversationMessagesDTO>>(response);
  return payload.data;
}

export async function getChatRuntimeDefaults(): Promise<ChatRuntimeDefaultsDTO> {
  const response = await fetch(createApiUrl('/api/chat/defaults', {
    includeAudioAnalysisProvider: 1,
  }), {
    credentials: 'include',
    method: 'GET',
  });

  const payload = await parseJsonResponse<unknown>(response);
  return chatRuntimeDefaultsSchema.parse(payload);
}

export async function listConversations(page = 1, pageSize = 20) {
  const response = await fetch(createApiUrl('/api/chat/conversations', { page, pageSize }), {
    credentials: 'include',
    method: 'GET',
  });

  return parseJsonResponse<ConversationListResponse>(response);
}

export async function renameConversation(conversationId: string, input: RenameConversationDTO) {
  const response = await fetch(createApiUrl(`/api/chat/conversations/${conversationId}`), {
    body: JSON.stringify(input),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'PATCH',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<ConversationDTO>>(response);
  return payload.data;
}

export async function stopChatTurn(turnId: string) {
  const response = await fetch(createApiUrl(`/api/chat/turns/${turnId}/stop`), {
    credentials: 'include',
    method: 'POST',
  });

  return parseJsonResponse<{ success: true }>(response);
}

export async function exportChatDocument(
  conversationId: string,
  documentId: string,
  format: ChatDocumentExportFormatDTO,
) {
  const response = await fetch(createApiUrl(`/api/chat/conversations/${conversationId}/documents/${documentId}/exports`), {
    body: JSON.stringify({ format }),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<ChatDocumentExportResultDTO>>(response);
  return payload.data;
}

export async function updateConversationRagMode(
  conversationId: string,
  input: UpdateConversationRagDTO,
) {
  const response = await fetch(createApiUrl(`/api/chat/conversations/${conversationId}/rag`), {
    body: JSON.stringify(input),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'PATCH',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<ConversationDTO>>(response);
  return payload.data;
}

export async function updateConversationWebSearchMode(
  conversationId: string,
  input: UpdateConversationWebSearchDTO,
) {
  const response = await fetch(createApiUrl(`/api/chat/conversations/${conversationId}/web-search`), {
    body: JSON.stringify(input),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'PATCH',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<ConversationDTO>>(response);
  return payload.data;
}

export async function listConversationFeedback(conversationId: string) {
  const response = await fetch(createApiUrl(`/api/chat/conversations/${conversationId}/feedback`), {
    credentials: 'include',
    method: 'GET',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<ConversationFeedbackListDTO>>(response);
  return payload.data;
}

export async function putMessageFeedback(messageId: string, input: UpsertMessageFeedbackInput) {
  const response = await fetch(createApiUrl(`/api/chat/messages/${messageId}/feedback`), {
    body: JSON.stringify(input),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    method: 'PUT',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<MessageFeedbackDTO>>(response);
  return payload.data;
}

export async function deleteMessageFeedback(messageId: string) {
  const response = await fetch(createApiUrl(`/api/chat/messages/${messageId}/feedback`), {
    credentials: 'include',
    method: 'DELETE',
  });

  await parseJsonResponse<{ success: true }>(response);
}
